import { WeekDay } from 'constants/recurrence';
import useLocalStorage from 'hooks/useLocalStorage';
import type { Day } from 'services/recurrenceRuleBuilder';
import type { DayRange, RangeEndType, RecurrenceType } from 'types/enums';
import {
  combineSlots,
  getBookingItemFields,
  getMinDate,
  type RawBookingItem,
} from 'utils/booking';
import moment from 'utils/dayjs';

const unique = <T,>(value: T, index: number, list: T[]) =>
  list.indexOf(value) === index;

// One of the booking's items, as far as the initial-values builder is concerned.
interface BookingItemInput extends RawBookingItem {
  endTime: string;
}

// The recurrence rules as stored on an existing booking. Values arrive as
// strings or numbers depending on whether they came from the API or the form.
export interface RecurrenceRulesInput {
  type?: RecurrenceType;
  dayRange?: DayRange;
  everyNumberOf?: number | string;
  count?: number | string;
  weekDay?: Day;
  weekDays?: Day[];
  dayOfMonthRange?: string;
  nthDayOfMonth?: number | string;
  weekNumber?: number | string;
  rangeEndType?: RangeEndType;
  endDate?: Date | string;
}

// A booking being loaded back into the widget — every field optional because
// this is also called with no argument at all to seed an empty form.
export interface StoredBookingInput {
  id?: number | string;
  venue?: { id?: number };
  clientService?: { id?: number | string };
  bookingHost?: { id?: number | string; label?: string } | null;
  purchaseOrder?: string;
  startTime?: Date | string;
  bookingItems?: BookingItemInput[];
  recurring?: boolean;
  recurrenceRules?: RecurrenceRulesInput;
}

const getInitialSlots = (bookingItems: BookingItemInput[] = []) => {
  const [date] = bookingItems
    .map(({ startTime }) => moment(startTime))
    .sort((a, b) => a.diff(b));

  return bookingItems
    .filter(({ startTime }) => moment(startTime).isSame(date, 'day'))
    .map(({ startTime, endTime }) => ({
      startTime: moment(startTime).format('HH:mm'),
      endTime: moment(endTime).format('HH:mm'),
    }));
};

// The recurrence defaults are resolved in the body rather than as destructuring
// defaults: they chain (weekDays from weekDay, endDate from all of them), and
// TypeScript cannot infer a parameter binding whose default refers to a sibling
// that refers back to it. Written out, each one is explicitly typed.
// Exactly the semantics of a destructuring default: only `undefined` falls back,
// so a null stored on the booking passes through as it did before.
const withDefault = <T,>(value: T | undefined, fallback: T): T =>
  value === undefined ? fallback : value;

const getInitialValues = ({
  id = '',
  venue: { id: venueId } = {},
  clientService: { id: service = '' } = {},
  bookingHost,
  purchaseOrder = '',
  startTime: date = moment().toDate(),
  bookingItems = [],
  recurring = false,
  recurrenceRules = {},
}: StoredBookingInput = {}) => {
  const recurrenceType: RecurrenceType = withDefault(
    recurrenceRules.type,
    'daily'
  );
  const dayRange: DayRange = withDefault(recurrenceRules.dayRange, 'days');
  const everyNumberOf = withDefault(recurrenceRules.everyNumberOf, '1');
  const count = withDefault(recurrenceRules.count, '2');
  const weekDay: Day = withDefault(
    recurrenceRules.weekDay,
    WeekDay[(moment(date).day() + 6) % 7]
  );
  const weekDays: Day[] = withDefault(recurrenceRules.weekDays, [weekDay]);
  const dayOfMonthRange = withDefault(
    recurrenceRules.dayOfMonthRange,
    'weekNumber' in recurrenceRules ? 'nthWeekdayOfMonth' : 'nthDayOfMonth'
  );
  const nthDayOfMonth = withDefault(
    recurrenceRules.nthDayOfMonth,
    moment(date).date()
  );
  const weekNumber = withDefault(recurrenceRules.weekNumber, '1');
  const rangeEndType: RangeEndType = withDefault(
    recurrenceRules.rangeEndType,
    'count' in recurrenceRules ? 'count' : 'date'
  );
  const endDate = withDefault(
    recurrenceRules.endDate,
    getMinDate(moment(date).toDate(), {
      type: recurrenceType,
      dayRange,
      everyNumberOf,
      weekDay,
      weekDays,
      dayOfMonthRange,
      nthDayOfMonth,
      weekNumber,
    })
  );

  return {
    id: String(id),
    venueId,
    spaceIds: bookingItems.map(({ space: { id } = {} }) => id!).filter(unique),
    service: String(service),
    host: bookingHost?.id
      ? { value: Number(bookingHost.id), label: bookingHost.label }
      : {},
    purchaseOrder: String(purchaseOrder),
    date: moment(date).startOf('day').toDate(),
    slots: combineSlots(getInitialSlots(bookingItems)),
    dates: [],
    bookingItems: getBookingItemFields(bookingItems),
    recurrence: {
      enabled: recurring,
      type: recurrenceType,
      dayRange,
      everyNumberOf: String(everyNumberOf),
      rangeEndType,
      endDate: moment(endDate).startOf('day').toDate(),
      count: String(count),
      weekDay,
      weekDays,
      dayOfMonthRange,
      nthDayOfMonth: Number(nthDayOfMonth),
      weekNumber: String(weekNumber),
      checked: recurring,
    },
  };
};

export { getInitialValues };

// The in-progress booking as it is mirrored into localStorage. Derived from the
// initial-values builder so the two cannot drift.
export type StoredBooking = ReturnType<typeof getInitialValues>;

const useStoredBooking = (defaultValue: StoredBooking = getInitialValues()) => {
  return useLocalStorage<StoredBooking>('booking', defaultValue);
};

export default useStoredBooking;
